// Reverse map: daemon_name → kill-switch key.
//
// The Settings → Connected Services panel groups daemons by SERVICE
// (Gmail, Calendar, MacBook Bridge, Discord …) and offers ONE toggle
// per service. The Agents page lists daemons INDIVIDUALLY and needs
// the inverse lookup so each row can answer "is my service paused?"
// from the cached app_settings toggle row.
//
// Source of truth on which daemons belong to which toggle is the
// ConnectedServicesCard SERVICES array — this file mirrors that
// catalogue. A future contributor adding a new toggleable daemon
// needs to update BOTH lists; the unit test in
// `daemonKillSwitch.test.ts` will fail if they drift.
//
// Daemons WITHOUT an entry here are simply "not toggleable" — their
// status comes directly from the heartbeat classifier.
//
// Catalogue asymmetry vs ConnectedServicesCard (R4 reviewer S1):
//   - Gmail tile lists 3 daemons (gmail_parser, gmail_watch_renewal,
//     email_drafts_writeback) but `email_triage_enabled` ALSO gates
//     workers_email_triage_classify + workers_email_triage_draft. The
//     tile shows the worst of three for the "is Gmail healthy?" aggregate;
//     /agents pauses all five. Both views are correct.
//   - Calendar tile lists parser + renewal; `calendar_enabled` ALSO gates
//     workers_focus_guardian. Same pattern.
// This is intentional — the tile aggregates the "user-visible" daemons,
// the kill switch reaches everything sharing the secret.

export type ToggleKey =
  | 'bridge_capture_enabled'
  | 'bridge_imessage_enabled'
  | 'bridge_whatsapp_enabled'
  | 'embeddings_enabled'
  | 'email_triage_enabled'
  | 'commission_enabled'
  | 'calendar_enabled'
  | 'discord_bot_enabled';

export const TOGGLE_KEYS: readonly ToggleKey[] = [
  'bridge_capture_enabled',
  // Per-channel bridge kill switches (migration 0042). bridge_capture_enabled
  // stays as the (dormant, default-on) master; these are the surfaced controls.
  'bridge_imessage_enabled',
  'bridge_whatsapp_enabled',
  'embeddings_enabled',
  'email_triage_enabled',
  'commission_enabled',
  'calendar_enabled',
  'discord_bot_enabled',
] as const;

const DAEMON_TO_TOGGLE: Record<string, ToggleKey> = {
  // MacBook Bridge — iMessage and WhatsApp are INDEPENDENT (0042): each daemon
  // maps to its own per-channel switch so Settings shows + controls them apart.
  bridge_imessage: 'bridge_imessage_enabled',
  bridge_whatsapp: 'bridge_whatsapp_enabled',

  // Gmail / Email Triage — note the toggle is named after the agent
  // (email_triage_enabled), which controls all three Gmail-flavored
  // workers including the watch-renewal helper.
  workers_gmail_parser: 'email_triage_enabled',
  workers_gmail_watch_renewal: 'email_triage_enabled',
  workers_email_triage_classify: 'email_triage_enabled',
  workers_email_triage_draft: 'email_triage_enabled',
  workers_email_drafts_writeback: 'email_triage_enabled',

  // Google Calendar — parser, watch-renewal, and focus guardian all
  // share calendar_enabled.
  workers_calendar_parser: 'calendar_enabled',
  workers_calendar_renewal: 'calendar_enabled',
  workers_focus_guardian: 'calendar_enabled',

  // Discord poster pushes notifications outbound; the bot listener
  // stays alive even when toggled off (operator can still hit
  // /status). Only the poster is gated.
  workers_discord_poster: 'discord_bot_enabled',

  // Embeddings backfill — global on/off.
  workers_embeddings_backfill: 'embeddings_enabled',

  // Commission agent.
  workers_commission_agent: 'commission_enabled',
};

/** Returns the toggle key gating this daemon, or null if the daemon
 *  is not user-toggleable. */
export function daemonKillSwitchKey(daemonName: string): ToggleKey | null {
  return DAEMON_TO_TOGGLE[daemonName] ?? null;
}

/** Build a map of toggle key → boolean from raw app_settings rows. */
export function buildToggleStateMap(
  rows: Array<{ key: string; value: string | null }>,
): Map<ToggleKey, boolean> {
  const out = new Map<ToggleKey, boolean>();
  for (const k of TOGGLE_KEYS) out.set(k, true); // default true (matches seed)
  let matched = 0;
  for (const r of rows) {
    if ((TOGGLE_KEYS as readonly string[]).includes(r.key)) {
      out.set(r.key as ToggleKey, r.value === 'true');
      matched += 1;
    }
  }
  // R4 reviewer N2: a steady-state owner SELECT should see all TOGGLE_KEYS
  // seeded rows (the 0032 six + the 0042 two). If we get fewer, RLS is masking
  // some — surface that loudly in dev so a future migration regression doesn't
  // silently render every daemon as "not paused."
  if (matched < TOGGLE_KEYS.length && typeof window !== 'undefined' && import.meta.env?.DEV) {
    console.warn(
      `daemonKillSwitch: only ${matched}/${TOGGLE_KEYS.length} toggle rows visible. ` +
        'RLS may have hidden some — check migrations 0032/0042 + auth state.',
    );
  }
  return out;
}

/** True iff this daemon's gating toggle is currently off. Falsey for
 *  daemons with no toggle (always returns false). */
export function isDaemonPaused(daemonName: string, toggleState: Map<ToggleKey, boolean>): boolean {
  const key = daemonKillSwitchKey(daemonName);
  if (!key) return false;
  return toggleState.get(key) === false;
}
